import { ApiError } from "./api-client";

export type ValidationError = {
  type: string;
  message: string;
  line?: number;
};

// Normalize whatever the save endpoint threw into the shape the UI renders.
// The save endpoint emits three response shapes:
//   • 400 validation: `{ errors: ValidationError[] }`     — markdown / frontmatter problems
//   • 4xx generic:    `{ error: "some_code" }`            — auth, slug mismatch, etc.
//   • 5xx crash:      `{ error: "internal_error" }`       — code only, never details
// The 5xx body carries no message on purpose: middleware/error.ts logs the
// exception to Workers observability and returns the bare code, because
// echoing err.message leaks drizzle / D1 / fetch internals. So a 5xx here can
// only show the code — read the Worker logs for the cause.
export function parseValidationErrors(e: unknown): ValidationError[] {
  if (!(e instanceof ApiError)) {
    return [{ type: "unknown", message: (e as Error).message }];
  }

  const payload =
    e.payload && typeof e.payload === "object"
      ? (e.payload as { errors?: ValidationError[]; error?: string })
      : null;

  if (payload?.errors && payload.errors.length > 0) return payload.errors;

  if (e.status >= 500) {
    return [{ type: "server", message: payload?.error ?? "server_error" }];
  }

  // Preserve the raw server-side error code as the `type` so the UI panel
  // surfaces unknown codes verbatim (instead of collapsing every 4xx into
  // "request"). Falls back to `unknown` only when the response had no body.
  return [
    // e.message is the Japanese copy built by apiErrorMessage.
    { type: payload?.error ?? "unknown", message: e.message },
  ];
}
